import React, { useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { RichDataTable } from "../../../../components/table/RichDataTable";
import { Column } from "primereact/column";
import {
  PageHeight40,
  PageTitle,
  createSortByAttr,
  scrollToTop,
  toUIDate,
  STATUS_LABELS,
  STATUS_IDS,
} from "../../../../components/Utils";
import { FormMenu } from "../../../../components/Buttons";
import ItemListManager from "../../../../components/ItemListManager";
import MSAuthenticatorTaskMenu from "../../components/itConnections/MSAuthenticatorTaskMenu";

import { connect } from "react-redux";
import {
  setSelectedMSAuthenticatorTasks,
  closeMSAuthenticatorTasks,
  deleteMSAuthenticatorTask,
} from "../../actions";
import { columnStyle, renderNgumUserIDColumn, renderRequesterColumn, renderUserNameColumn, actionColumnStyle } from "../../components/Utils";

const CONNECTION_TYPE = "MS";

const mapStateToProps = (state) => {
  return {
    isLoading: state.task.loading,
    data: state.task.msAuthenticatorTasks,
    selection: state.task.selectedMSAuthenticatorTasks,
  };
};
const mapDispatchToProps = (dispatch) => ({
  setSelection: (...args) => dispatch(setSelectedMSAuthenticatorTasks(...args)),
  closeTasks: (...args) => dispatch(closeMSAuthenticatorTasks(...args)),
  deleteTask: (...args) => dispatch(deleteMSAuthenticatorTask(...args)),
});

const MSAuthenticatorTaskList = ({
  className = "",
  isLoading,
  data,
  selection,
  setSelection,
  closeTasks,
  deleteTask,
  rows = 10,
}) => {
  useEffect(() => {
    scrollToTop();
  }, []);

  const onClose = useCallback((task, onHide) => {
    if (!task) return;
    closeTasks([task], onHide);
  }, [closeTasks]);

  const onDelete = useCallback((task) => {
    deleteTask(task);
  }, [deleteTask]);

  const renderItemTable = useCallback((items, actionTemplate) => {
    const taskItems = items
      .map((item) => ({
        ...item,
        user: item.user.split(",").join(" "),
        requester: `${item.requester || ""}`.split(",").join(" "),
        requestDate: toUIDate(item.request_date, "YYYY-MM-DDTHH:mm:ss:SSSZ"),
        userInCharge: `${item.userInCharge || ""}`.split(",").join(" "),
        msAuthStatus: STATUS_LABELS[STATUS_IDS.NEW],
      }))
      .sort(createSortByAttr("request_date", true, true));
    return (
      <RichDataTable
        paginator
        paginatorLeftProps={{
          emptyText: "Keine MS Authenticator Aufgaben",
          itemName: "MS Authenticator Aufgabe",
          itemNamePlural: "MS Authenticator Aufgaben"
        }}
        data={taskItems}
        rows={rows}
        selection={selection}
        setSelection={setSelection}
        removableSort
        sortMode="multiple"
        isLoading={isLoading}
        noConnectionTaskTypeFiltering
      >
        <Column selectionMode="multiple" style={{ width: "3em" }} />
        <Column
          field="user"
          header="Mitarbeiter"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          body={renderUserNameColumn}
        />
        <Column
          field="adCellNumber"
          header="Handynummer"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          style={{ ...columnStyle, width: "10em" }}
          body={(item) => <span style={{wordBreak: "break-word"}}>{item.adCellNumber || ""}</span>}
        />
        <Column
          field="vf_email"
          header="Vodafone E-Mail"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          style={{ ...columnStyle, wordBreak: "break-word" }}
          body={(item) => <span style={{wordBreak: "break-word"}}>{item.userData.vf_email || ""}</span>}
        />
        <Column
          field="ngumUserId"
          header="nGUM UserID"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          style={{ ...columnStyle }}
          body={renderNgumUserIDColumn}
        />
        <Column
          field="requester"
          header="Beantrager"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          style={{ ...columnStyle, width: "12em" }}
          body={renderRequesterColumn}
        />
        <Column
          field="requestDate"
          header="Erstelldatum"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          style={{ ...columnStyle, width: "10em", textAlign: "center" }}
        />
        <Column
          field="msAuthStatus"
          header="Status"
          sortable={taskItems.length > 1}
          filter // ={taskItems.length > rows}
          filterMatchMode="contains"
          style={{ ...columnStyle, width: "9em" }}
        />
        <Column
          body={actionTemplate}
          style={{ ...actionColumnStyle, textAlign: "center", width: "7em" }}
        />
      </RichDataTable>
    );
  }, [rows, selection, setSelection, isLoading]);

  const renderItemForm = useCallback((item, onHide) => {
    return (
      <div>
        <div className="w3-large">{`Wollen Sie die Aufgabe wirklich abschließen?`}</div>
        <FormMenu
          className="margin-big-top w3-border-top pad-big"
          style={{
            marginLeft: "-1em",
            marginRight: "-1em",
            marginBottom: "-1em",
          }}
          onSave={() => onClose(item, onHide)}
          onCancel={onHide}
          saveBtnName="closeMSAuthenticatorTaskBtn"
          saveBtnLabel={"Schließen"}
        />
      </div>
    );
  }, [onClose]);

  const getItemFormTitle = useCallback(() => {
    let title = "MS Authenticator-Task schießen";
    return <h3 className="no-margin">{title}</h3>;
  }, []);

  const title = (
    <PageTitle>
      <MSAuthenticatorTaskMenu
        className="w3-right"
        selectedTasks={selection}
        closeTasks={closeTasks}
        items={data}
      />
      <h3 className="no-margin">{"MS Authenticator Aufgaben"}</h3>
    </PageTitle>
  );

  return (
    <PageHeight40 className={className}>
      <ItemListManager
        key={Math.random()}
        title={title}
        addBtnHidden
        items={data}
        itemPanelStyle={{ width: "600px" }}
        getItemFormTitle={getItemFormTitle}
        renderItemTable={renderItemTable}
        renderItemForm={renderItemForm}
        namePrefix={`${CONNECTION_TYPE}Connection`}
        itemDeleteable
        deleteTitle={`MS Authenticator-Aufgabe löschen`}
        getDeleteText={(item) =>
          `Wollen Sie diese Aufgabe unwiderruflich löschen?`
        }
        onDelete={onDelete}
      />
    </PageHeight40>
  );
};

MSAuthenticatorTaskList.propTypes = {
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  data: PropTypes.array,
  selection: PropTypes.array,
  setSelection: PropTypes.func.isRequired,
  closeTasks: PropTypes.func.isRequired,
  deleteTask: PropTypes.func.isRequired,
  rows: PropTypes.number,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MSAuthenticatorTaskList);
